/* global React, Icon, Card, CardHead, Button, Badge, Segmented, Tabs, Modal, KPI, SectionTitle, EmptyState, toast, fmt, fmt0, buildAiiScenarioView */
// ============================================================
// PLUMME 2.0 — AI Innsikt (Økonomisk rådgiver)
// SuperPlumma som en AI-drevet finanssjef (CFO) for små bedrifter.
// ============================================================
const { useState: aiUseState } = React;

const AII_STATUS_COLOR = { green: 'var(--success)', amber: 'var(--warning)', red: 'var(--error)', none: 'var(--muted)' };
// Hvor brukeren faktisk kan fylle inn det datagrunnlaget mangler. `previousYear` har
// ingen rute: fjorarstall kan ikke registreres pa en side, de kommer nar aret er fort.
const AII_BASIS_ROUTE = {
  postedIncome: 'faktura',
  postedCosts: 'nyttkjop',
  openItems: 'fakturaliste',
  previousYear: null,
  customerSplit: 'kunder',
  completeVouchers: 'innboks',
};
const AII_MONITOR_ICON = {
  liquidity: 'wallet',
  receivables: 'fileText',
  costs: 'receipt',
  tax: 'percent',
  quality: 'database',
  sales: 'trendUp',
};
function AiiHelpTerm({ title, help, children, className }) {
  if (!help) return <React.Fragment>{children}</React.Fragment>;
  return <span className={className} data-help-title={title || children} data-help={help}>{children}</span>;
}
// Ingen fallback-buffer: 180 000 var designets eksempeltall. Har ikke modellen regnet
// ut en buffer, tegner vi ingen bufferlinje i stedet for a vise et tall vi har funnet pa.
const aiiPlumSrc = () => (window.__resources && window.__resources.superPlummaHero) || 'src/img/SuperPlumma.png';

// ---------- Delte byggeklosser fra designet ----------
// Teller opp til verdien én gang ved mount. Guard-timeouten sikrer sluttverdien
// selv om requestAnimationFrame strupes i en bakgrunnsfane.
function AiCountUp({ value, dur = 1000, format }) {
  const [shown, setShown] = aiUseState(0);
  const started = React.useRef(false);
  React.useEffect(() => {
    if (started.current) return undefined;
    started.current = true;
    let raf;
    const start = performance.now();
    // NB: ikke kall variabelen `t` — den ville skygget for den globale t().
    const tick = (now) => {
      const progress = Math.min(1, (now - start) / dur);
      setShown(value * (1 - Math.pow(1 - progress, 3)));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const guard = setTimeout(() => setShown(value), dur + 250);
    return () => { cancelAnimationFrame(raf); clearTimeout(guard); };
  }, [value]);
  return <React.Fragment>{format ? format(shown) : Math.round(shown)}</React.Fragment>;
}

// Helsekortet i designet viser status først og tallet sekundært. Designet har bare
// den friske varianten; tonene under trengs fordi en reell score kan være svak.
const aiHealthTone = (score) => {
  if ((score == null || !Number.isFinite(Number(score)))) return { tone: 'none', badge: t('adv.health.badge.none'), word: t('adv.health.word.none') };
  const value = Number(score);
  if (value >= 80) return { tone: 'green', badge: t('adv.health.badge.good'), word: t('adv.health.word.good') };
  if (value >= 60) return { tone: 'amber', badge: t('adv.health.badge.stable'), word: t('adv.health.word.stable') };
  return { tone: 'red', badge: t('adv.health.badge.weak'), word: t('adv.health.word.weak') };
};
function AiHealthCard({ score }) {
  const hasScore = score != null && Number.isFinite(Number(score));
  const meta = aiHealthTone(score);
  return <div className={'aii-health-card tone-' + meta.tone} data-help-title={t('adv.health.title')} data-help={t('adv.help.healthCard')}>
    <span className="aii-health-badge"><span className="aii-health-dot" />{meta.badge}</span>
    <div className="aii-health-word">{meta.word}</div>
    <div className="aii-health-num">{hasScore ? <AiCountUp value={Number(score)} /> : '—'}{hasScore && <span>/100</span>}</div>
  </div>;
}

// Kategoriene kan komme fra den lokale modellen eller servermodellen — begge sender
// samme stabile `key` og samme faste norske tekst for `note`/`val` når data mangler.
// Oversettelsen skjer derfor her i stedet for å stole på at kilden allerede har
// oversatt den; en allerede oversatt tekst matcher rett og slett ingen oppslag under.
const AII_HEALTH_NOTE_KEY = {
  'Ingen åpne leverandørkrav registrert': 'adv.health.note.noOpenPayables',
  'Kundefordringer målt mot åpne leverandørkrav': 'adv.health.note.receivablesVsPayables',
  'Fjorårets omsetning er 0; prosentvis vekst kan ikke beregnes': 'adv.health.note.zeroPrevYearRevenue',
  'Trenger fjorårstall for sammenligning': 'adv.health.note.needsPrevYear',
  'Omsetningsendring mot samme år i fjor': 'adv.health.note.revenueChangeVsPrevYear',
  'Resultatmargin fra resultatrapporten': 'adv.health.note.marginFromReport',
  'Ingen bokført omsetning i valgt år': 'adv.health.note.noBookedRevenue',
  'Andel av salg fra største kunde': 'adv.health.note.largestCustomerShare',
  'Trenger fakturasalg per kunde': 'adv.health.note.needsInvoicesPerCustomer',
};
const AII_HEALTH_VAL_KEY = { 'Ingen gjeld': 'adv.health.note.noDebt', 'Ikke nok data': 'adv.health.badge.none', 'Ukjent': 'adv.health.note.unknown' };
const aiiHealthCategoryName = (c) => t('adv.health.category.' + c.key, c.name);
const aiiHealthCategoryNote = (c) => AII_HEALTH_NOTE_KEY[c.note] ? t(AII_HEALTH_NOTE_KEY[c.note], c.note) : c.note;
const aiiHealthCategoryVal = (c) => AII_HEALTH_VAL_KEY[c.val] ? t(AII_HEALTH_VAL_KEY[c.val], c.val) : c.val;

// Prioriteringskortet erstatter det flate anbefalingskortet. `why` kommer fra
// narrativlaget som { key, vars } og oversettes her; `title` og `gainNote` er
// fortsatt valgfrie.
// Effekttypen sier hva slags tall `impact` er. Uten den ser tre anbefalinger med
// hvert sitt kronebeløp ut som noe man kan legge sammen — det er de ikke: frigjort
// likviditet, spart kostnad og sikret fradrag er tre ulike størrelser.
const AII_EFFECT_ICON = { liquidity: 'wallet', cost: 'trendDown', tax: 'percent', risk: 'alertTri', income: 'trendUp', time: 'clock' };
function AiiEffectTag({ type }) {
  if (!type || !AII_EFFECT_ICON[type]) return null;
  return <span className="aii-effect-tag" data-help-title={t('adv.effect.' + type)} data-help={t('adv.help.effect.' + type)}>
    <Icon name={AII_EFFECT_ICON[type]} size={11} />{t('adv.effect.' + type)}
  </span>;
}
// Sikkerhet per anbefaling i stedet for én samlet prosent for hele analysen.
function AiiConfidenceTag({ level }) {
  const [open, setOpen] = aiUseState(false);
  if (!level || !['high', 'medium', 'low'].includes(level)) return null;
  const tone = level === 'high' ? 'green' : level === 'medium' ? 'amber' : 'muted';
  const icon = level === 'high' ? 'checkCircle' : level === 'medium' ? 'alertCircle' : 'alertTri';
  return <div className="aii-conf-tag-wrap">
    <button type="button" className={'aii-conf-tag tone-' + tone} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
      <Icon name={icon} size={12} />{t('adv.confLevel.' + level)}
    </button>
    {open && <div className="aii-conf-tag-explain" role="note">{t('adv.confLevel.' + level + 'Help')}</div>}
  </div>;
}

const AII_STATUS_STYLE = {
  new: { tone: 'plum', icon: 'sparkle' },
  snoozed: { tone: 'muted', icon: 'clock' },
  done: { tone: 'green', icon: 'check' },
  dismissed: { tone: 'muted', icon: 'x' },
};
function AiiStatusPill({ resolved }) {
  if (!resolved || resolved.status === 'new') return null;
  const style = AII_STATUS_STYLE[resolved.status];
  return <span className={'aii-status-pill tone-' + style.tone} data-help={t('aiHelp.advisor.taskStatus.body')} data-help-title={t('aiHelp.advisor.taskStatus.title')}>
    <Icon name={style.icon} size={12} />
    {t('adv.status.' + resolved.status)}
    {resolved.status === 'snoozed' && resolved.snoozeUntil ? ` · ${resolved.snoozeUntil}` : ''}
  </span>;
}

// Statusmenyen setter bare et notat. Den sender ingenting og bokfører ingenting —
// derfor står det «Marker som gjort», ikke «Fullfør».
function AiiStatusMenu({ rec, resolved, onSet }) {
  const [open, setOpen] = aiUseState(false);
  const [dismissing, setDismissing] = aiUseState(false);
  const close = () => { setOpen(false); setDismissing(false); };
  const snoozeDate = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toISOString().slice(0, 10);
  };
  return <div className="aii-prio-sec-wrap">
    <button type="button" className="aii-prio-sec-btn" onClick={() => setOpen((v) => !v)}
      aria-haspopup="true" aria-expanded={open} aria-label={t('adv.status.more')}>⋯</button>
    {open && <div className="aii-prio-sec-menu" role="menu">
      {dismissing
        ? <React.Fragment>
          <div className="aii-dismiss-lbl">{t('adv.status.whyNotRelevant')}</div>
          {(globalThis.PlummeAiAdvisorStatus?.DISMISS_REASONS || []).map((reason) => <button key={reason} role="menuitem"
            onClick={() => { onSet({ status: 'dismissed', note: t('adv.status.reason.' + reason) }); close(); }}>
            {t('adv.status.reason.' + reason)}
          </button>)}
          <button role="menuitem" className="aii-dismiss-cancel" onClick={() => setDismissing(false)}>{t('common.cancel')}</button>
        </React.Fragment>
        : <React.Fragment>
          {resolved.status !== 'new' && <button role="menuitem" onClick={() => { onSet({ status: 'new' }); close(); }}>{t('adv.status.reopen')}</button>}
          {/* «Merk gjort» er brukerens eget notat. Selve utførelsen ligger i den
              primære knappen på kortet, som går via plattform-routeren. */}
          <button role="menuitem" onClick={() => { onSet({ status: 'snoozed', snoozeUntil: snoozeDate(7) }); close(); }}>{t('adv.status.snooze7')}</button>
          <button role="menuitem" onClick={() => { onSet({ status: 'snoozed', snoozeUntil: snoozeDate(30) }); close(); }}>{t('adv.status.snooze30')}</button>
          <button role="menuitem" onClick={() => { onSet({ status: 'done' }); close(); }} data-help={t('helpTopics.aiTask.body')} data-help-title={t('helpTopics.aiTask.title')}>{t('adv.status.markDone')}</button>
          <button role="menuitem" onClick={() => setDismissing(true)}>{t('adv.status.notRelevant')}</button>
        </React.Fragment>}
    </div>}
  </div>;
}

function AiPrioCard({ p, rank, onAct, resolved, onSetStatus }) {
  const [whyOpen, setWhyOpen] = aiUseState(false);
  const why = Array.isArray(p.why) ? p.why.filter(Boolean) : [];
  return <div className={'aii-prio ' + p.lvl}
    data-help={t('aiHelp.advisor.recommendation.body')} data-help-title={t('aiHelp.advisor.recommendation.title')}>
    <div className="aii-prio-head">
      <span className="aii-prio-ico"><Icon name={p.icon} size={19} /></span>
      {rank != null && <span className="aii-prio-rank">{rank}</span>}
      <span className="aii-prio-badge"><Icon name={p.lvl === 'crit' ? 'alertTri' : p.lvl === 'imp' ? 'alertCircle' : 'sparkle'} size={12} />{p.badge}</span>
      {resolved && <AiiStatusPill resolved={resolved} />}
      {onSetStatus && <AiiStatusMenu rec={p} resolved={resolved} onSet={onSetStatus} />}
    </div>
    {/* Situasjonen har endret seg siden brukeren tok stilling. Vi skjuler ikke rådet,
        men sier hvorfor det er tilbake. */}
    {resolved?.stale && <div className="aii-prio-stale"><Icon name="alertCircle" size={13} />{t('adv.status.stale')}</div>}
    {p.title && <div className="aii-prio-title">{p.title}</div>}
    <div className="aii-prio-text">{p.text}</div>
    <div className="aii-prio-gain has-action">
      <span className="lbl">{p.impactLbl}</span>
      <span className="val">{p.impact}</span>
      {p.gainNote && <span className="note">{p.gainNote}</span>}
      <Button size="sm" icon="sparkle" onClick={onAct} data-help={t('helpTopics.aiTask.body')} data-help-title={t('helpTopics.aiTask.title')}>{t('adv.handoff.start')}</Button>
    </div>
    {(p.effectType || p.confidence || p.discussWithAccountant) && <div className="aii-prio-tags">
      <AiiEffectTag type={p.effectType} />
      <AiiConfidenceTag level={p.confidence} />{p.discussWithAccountant && window.AiiAccountantTag && <window.AiiAccountantTag />}
    </div>}
    {why.length > 0 && <React.Fragment>
      <button type="button" className="aii-prio-why-btn" onClick={() => setWhyOpen(true)} aria-haspopup="dialog">
        <Icon name="sparkle" size={13} />{t('adv.prio.why')}<Icon name="chevRight" size={14} />
      </button>
      {whyOpen && <Modal open onClose={() => setWhyOpen(false)} title={t('adv.prio.whyTitle')} max={440} center portal className="aii-prio-why-modal"
        footer={<Button onClick={() => setWhyOpen(false)}>{t('common.close')}</Button>}>
        <p className="aii-prio-why-summary">{p.text}</p>
        <ul className="aii-prio-why">{why.map((w, i) => <li key={i}>{typeof w === 'string' ? w : t('adv.why.' + w.key, aiiVars(w.vars))}</li>)}</ul>
      </Modal>}
    </React.Fragment>}
  </div>;
}

// ---------- Narrativlaget (spor B) ----------
// Modellen leverer { key, vars } — aldri ferdig tekst. Oversettelsen skjer her, så
// regnskapsdomenet slipper å eie språk.
function AiVerdict({ verdict, tag, details, className = '' }) {
  if (!verdict) return null;
  return <div className={'aii-verdict tone-' + (verdict.tone || 'amber') + (details ? ' has-details' : '') + (className ? ' ' + className : '')}>
    <div className="aii-verdict-main">
      <span className="aii-verdict-avatar"><img src={aiiPlumSrc()} alt="SuperPlumma" /></span>
      <div className="aii-verdict-body">
        <div className="aii-verdict-tag"><Icon name="sparkle" size={14} />{tag || t('adv.verdict.tag')}</div>
        <p className="aii-verdict-text">{t('adv.verdict.' + verdict.key, aiiVars(verdict.vars))}</p>
      </div>
    </div>
    {details && <div className="aii-verdict-details">{details}</div>}
  </div>;
}
function AiWhy({ title, sub, items, showHeading = true }) {
  if (!items?.length) return null;
  return <React.Fragment>
    {showHeading && <AiSecHead icon="info" title={title || t('adv.why.title')} sub={sub} />}
    <div className="aii-why-list">
      {items.map((item, i) => <div key={i} className={'aii-why-row tone-' + (item.tone || 'amber')}>
        <span className="aii-why-dot" />
        <span className="aii-why-txt">{t('adv.why.' + item.key, aiiVars(item.vars))}</span>
        {item.amount != null && <span className="aii-why-amt">{fmt(item.amount)}</span>}
      </div>)}
    </div>
  </React.Fragment>;
}
function AiMoves({ title, sub, moves, onAct }) {
  if (!moves?.length) return null;
  return <React.Fragment>
    <AiSecHead icon="sparkle" title={title || t('adv.moves.title')} sub={sub} />
    <div className="aii-move-grid">
      {moves.map((move, i) => <div key={i} className="aii-move">
        <div className="aii-move-top">
          <span className="aii-move-ico"><Icon name={move.icon || 'sparkle'} size={19} /></span>
          <span className="aii-move-rank">{i + 1}</span>
        </div>
        <div className="aii-move-title">{t('adv.moves.' + move.key, aiiVars(move.vars))}</div>
        <div className="aii-move-gain">
          {move.gain != null && <span className="val">{fmt(move.gain)}</span>}
          <span className="note">{t('adv.gain.' + move.gainNote)}</span>
        </div>
        <Button size="sm" icon="sparkle" onClick={() => onAct && onAct(move)}>{t('adv.moves.go')}</Button>
      </div>)}
    </div>
  </React.Fragment>;
}
// Tallverdier i vars formatteres som beløp/prosent av UI-laget, ikke av modellen.
const aiiVars = (vars) => {
  const out = {};
  Object.entries(vars || {}).forEach(([key, value]) => {
    out[key] = /^(amount|buffer|shortfall|vat|income|costs|result|gain)$/.test(key) && Number.isFinite(Number(value))
      ? fmt(Number(value))
      : value;
  });
  return out;
};

// Grafer og tabeller flyttes bak «Vis dokumentasjon» — analysen først, tallgrunnlaget etter.
// `defaultOpen` er på inntil dom/forklaring/grep ligger over seksjonen: uten noe over
// den ville en lukket seksjon gitt en tom fane.
function AiDocs({ label, defaultOpen = false, children }) {
  const [open, setOpen] = aiUseState(defaultOpen);
  return <div className={'aii-docs' + (open ? ' open' : '')}>
    <button type="button" className="aii-docs-toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
      <span className="aii-docs-lbl"><Icon name="fileText" size={17} />{label || t('adv.docs.default')}</span>
      <span className="aii-docs-hint">{open ? t('adv.docs.hide') : t('adv.docs.hint')}<Icon name={open ? 'chevUp' : 'chevDown'} size={17} /></span>
    </button>
    {open && <div className="aii-docs-body">{children}</div>}
  </div>;
}

// ---------- Økonomisk kalender ----------
// Bygges av reelle frister fra hjem-modellen, gruppert per måned. Ingen frister
// betyr tom seksjon, ikke eksempeldatoer.
const aiCalendarTone = (deadline, now) => {
  if (deadline.status === 'ok') return 'opp';
  if (deadline.status === 'snart') return 'imp';
  const due = deadline.dueDate ? new Date(deadline.dueDate) : null;
  return due && due.getTime() < now ? 'crit' : 'info';
};
// Fristene i hjem-modellen er alle lovpålagte — MVA, skattemelding, årsregnskap.
// Rutene og beløpene finnes ikke på fristen selv, så de kobles på her: dette er
// presentasjon, ikke regnskapslogikk.
const AII_CAL_ROUTE = { taxSubmitted: 'skattemelding', annualAccountsSubmitted: 'arsregnskap' };
const aiCalendarRoute = (deadline) =>
  AII_CAL_ROUTE[deadline.id] || (String(deadline.id).startsWith('term-') || deadline.id === 'annual' ? 'innsendinger' : null);
const aiCalendarAmount = (deadline, publicFees) => {
  if (String(deadline.id).startsWith('term-') || deadline.id === 'annual') return publicFees?.vatDue ?? null;
  if (deadline.id === 'taxSubmitted') return publicFees?.taxBuffer ?? null;
  return null;
};
const aiDaysUntil = (dueDate, now = Date.now()) => {
  const dueParts = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dueDate || '')), current = new Date(now); // Deadlines are civil dates, not UTC instants.
  if (!dueParts || Number.isNaN(current.getTime())) return null;
  const dueDay = Date.UTC(Number(dueParts[1]), Number(dueParts[2]) - 1, Number(dueParts[3])), currentDay = Date.UTC(current.getFullYear(), current.getMonth(), current.getDate());
  return Math.round((dueDay - currentDay) / 86400000);
};
// Samme visningsnavn-logikk som dashboard.jsx sin lokale deadlineDisplayName —
// duplisert her fordi sidene lastes som separate route-scopede scripts og kan
// ikke dele lokale funksjoner. Gjenbruker de samme, allerede eksisterende nøklene.
const aiiDeadlineDisplayName = (deadline) => {
  const id = deadline?.id || '';
  if (id === 'shareholderRegisterSubmitted') return t('home.deadlines.name.shareholderRegister');
  if (id === 'taxSubmitted') return t('nav.taxReturn');
  if (id === 'annualAccountsSubmitted') return t('nav.annualAccounts');
  if (id === 'mvaSubmitted') return t('nav.filings');
  const vatTerm = String(id).match(/^term-(\d+)$/);
  if (vatTerm) return `${t('nav.filings')} (${vatTerm[1]})`;
  return deadline?.name || '';
};
const buildAiCalendar = (deadlines, publicFees, limit = 6) => {
  const now = Date.now();
  return (Array.isArray(deadlines) ? deadlines : [])
    .filter((d) => d && d.dueDate)
    .slice()
    .sort((a, b) => String(a.dueDate).localeCompare(String(b.dueDate)))
    .map((deadline) => ({
      id: deadline.id,
      label: aiiDeadlineDisplayName(deadline),
      date: PlummeI18n.date(deadline.dueDate, { day: 'numeric', month: 'short', year: 'numeric' }),
      days: aiDaysUntil(deadline.dueDate, now),
      tone: aiCalendarTone(deadline, now),
      done: deadline.status === 'ok',
      amount: aiCalendarAmount(deadline, publicFees),
      route: aiCalendarRoute(deadline),
    }))
    .slice(0, limit);
};

// ---------- Cashflow chart ----------
// `dataAfter` er den simulerte kurven etter et valgt tiltak. Er den satt, tegnes
// basiskurven stiplet og grå, og etter-kurven heltrukket — ellers ville brukeren ikke
// se hvilken av dem som er hva.
function AiCashflowChart({ data, buffer, dataAfter, afterLabel }) {
  const W = 860, H = 320, pad = { l: 16, r: 16, t: 22, b: 34 };
  const innerW = W - pad.l - pad.r, innerH = H - pad.t - pad.b;
  // Hover-punktet: tallene bak hver måned er ellers bare avlesbare fra aksen.
  const [hover, setHover] = React.useState(null);
  // Grafen ligger i en container med overflow:hidden, så en tooltip som alltid står
  // over punktet blir klippet vekk øverst i kurven og på ytterkantene. Vi måler den
  // faktiske boksen etter render og snur/forskyver den så den alltid er synlig.
  const wrapRef = React.useRef(null);
  const tipRef = React.useRef(null);
  const [tipFit, setTipFit] = React.useState(null);
  const max = Math.max(1, ...data.map((d) => d.balance), ...(dataAfter || []).map((d) => d.balance), buffer || 0) * 1.12;
  const min = Math.min(0, ...data.map((d) => d.balance), ...(dataAfter || []).map((d) => d.balance)) * 1.12;
  const x = (i) => pad.l + (data.length === 1 ? innerW / 2 : innerW * i / (data.length - 1));
  const y = (v) => pad.t + innerH * (1 - (v - min) / (max - min));
  const pts = data.map((d, i) => [x(i), y(d.balance)]);
  const afterPts = dataAfter ? dataAfter.map((d, i) => [x(i), y(d.balance)]) : null;
  const toPath = (list) => list.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const line = toPath(pts);
  const area = line + ` L ${x(data.length - 1).toFixed(1)} ${y(0).toFixed(1)} L ${x(0).toFixed(1)} ${y(0).toFixed(1)} Z`;
  const ticks = [min, min + (max - min) / 3, min + (max - min) * 2 / 3, max].map((v) => Math.round(v));
  const breach = buffer ? data.find((d) => d.balance < buffer) : null;
  const hoverIdx = hover != null && hover < data.length ? hover : null;
  React.useLayoutEffect(() => {
    if (hoverIdx == null) { setTipFit(null); return; }
    const wrap = wrapRef.current, tip = tipRef.current;
    if (!wrap || !tip) return;
    const box = wrap.getBoundingClientRect();
    const tipW = tip.offsetWidth, tipH = tip.offsetHeight;
    const px = pts[hoverIdx][0] / W * box.width;
    const py = pts[hoverIdx][1] / H * box.height;
    // Snu under punktet bare hvis det faktisk er plass der — ellers er toppen minst dårlig.
    const below = py - tipH - 14 < 0 && py + tipH + 14 <= box.height;
    let dx = 0;
    const left = px - tipW / 2;
    if (left < 4) dx = 4 - left;
    else if (left + tipW > box.width - 4) dx = box.width - 4 - (left + tipW);
    setTipFit({ below, dx });
  }, [hoverIdx, data.length, Boolean(afterPts)]);
  const tipStyle = {
    left: hoverIdx != null ? (pts[hoverIdx][0] / W * 100) + '%' : 0,
    top: hoverIdx != null ? (pts[hoverIdx][1] / H * 100) + '%' : 0,
  };
  if (tipFit) {
    tipStyle.transform = `translate(calc(-50% + ${tipFit.dx}px), ${tipFit.below ? '0' : '-100%'}) translateY(${tipFit.below ? 14 : -14}px)`;
  }
  return <div ref={wrapRef} style={{ position: 'relative', width: '100%', height: '100%' }}>
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" preserveAspectRatio="none" style={{ display: 'block' }}>
      <defs>
        <linearGradient id="aiCfFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--plum-400)" stopOpacity="0.32" />
          <stop offset="100%" stopColor="var(--plum-400)" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {/* NB: iterasjonsvariabelen kan ikke hete `t` — den ville skygget for den globale t(). */}
      {ticks.map((tick, i) => <g key={i}>
        <line x1={pad.l} x2={W - pad.r} y1={y(tick)} y2={y(tick)} stroke="var(--border)" strokeWidth="1" />
        <text x={pad.l + 2} y={y(tick) - 6} fontSize="12" fontWeight="700" fill="var(--muted-2)">{fmt0(tick)}</text>
      </g>)}
      {/* buffer line — bare når modellen faktisk har regnet ut en buffer */}
      {buffer > 0 && <line x1={pad.l} x2={W - pad.r} y1={y(buffer)} y2={y(buffer)} stroke="var(--warning)" strokeWidth="2" strokeDasharray="7 6" />}
      {buffer > 0 && <text x={W - pad.r} y={y(buffer) - 7} fontSize="12" fontWeight="800" fill="var(--warning)" textAnchor="end">{t('adv.cf.legendBuffer')} · {fmt0(buffer)}</text>}
      <path d={area} fill="url(#aiCfFill)" />
      <path d={line} fill="none" stroke={afterPts ? 'var(--border-strong)' : 'var(--plum-600)'} strokeWidth="3"
        strokeDasharray={afterPts ? '7 5' : undefined} strokeLinecap="round" strokeLinejoin="round" />
      {afterPts && <path d={toPath(afterPts)} fill="none" stroke="var(--success)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />}
      {pts.map((p, i) => {
        const low = buffer ? data[i].balance < buffer : false;
        // Usikre maneder markeres med apen sirkel: de har ingen bilag bak seg enna.
        const uncertain = data[i].certainty === 'uncertain';
        const dot = low ? 'var(--warning)' : afterPts ? 'var(--border-strong)' : 'var(--plum-600)';
        const active = hoverIdx === i;
        return <g key={i}>
          {active && <line x1={p[0]} x2={p[0]} y1={pad.t} y2={y(0)} stroke="var(--border-strong)" strokeWidth="1" strokeDasharray="3 4" opacity="0.7" />}
          <circle cx={p[0]} cy={p[1]} r={active ? 7 : low ? 5 : 4} fill={uncertain ? 'var(--card)' : dot} stroke={uncertain ? dot : 'var(--card)'} strokeWidth="2" style={{ transition: 'r .12s' }} />
          {/* Usynlig treffsone: selve punktet er for lite til å treffe med musen. */}
          <circle cx={p[0]} cy={p[1]} r="13" fill="transparent" style={{ cursor: 'pointer' }}
            onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover((h) => h === i ? null : h)} />
          <text x={p[0]} y={H - 14} fontSize="12.5" fontWeight={low || active ? 900 : 700} fill={low ? 'var(--warning)' : active ? 'var(--plum-700)' : 'var(--muted)'} textAnchor="middle">{data[i].m}</text>
        </g>;
      })}
      {afterPts && afterPts.map((p, i) => <circle key={'a' + i} cx={p[0]} cy={p[1]} r={hoverIdx === i ? 6 : 4} fill="var(--success)" stroke="var(--card)" strokeWidth="2" style={{ transition: 'r .12s' }} />)}
    </svg>
    {hoverIdx != null && <div ref={tipRef} className={'aii-cf-tooltip' + (tipFit?.below ? ' below' : '')} style={tipStyle}>
      <div className="aii-cf-tooltip-m">{data[hoverIdx].m}</div>
      <div className="aii-cf-tooltip-row"><span>{t('adv.cf.tipBalance')}</span><b>{fmt(data[hoverIdx].balance)}</b></div>
      {afterPts && <div className="aii-cf-tooltip-row after"><span>{afterLabel || t('adv.cf.simAfter')}</span><b>{fmt(dataAfter[hoverIdx].balance)}</b></div>}
      <div className="aii-cf-tooltip-row sub"><span>{t('adv.cf.tipIn')}</span><b>+{fmt(data[hoverIdx].inn || 0)}</b></div>
      <div className="aii-cf-tooltip-row sub"><span>{t('adv.cf.tipOut')}</span><b>−{fmt(data[hoverIdx].ut || 0)}</b></div>
      {buffer > 0 && data[hoverIdx].balance < buffer && <div className="aii-cf-tooltip-flag">{t('adv.cf.tipBelowBuffer')}</div>}
    </div>}
  </div>;
}

// Regnestykket bak sluttsaldoen, slik at tallet kan etterprøves i stedet for å måtte
// tros. `adj` fanger avrunding og gulvet på 0 i saldoen, så linjene alltid summerer seg.
function AiForecastMath({ data }) {
  if (!data.length) return null;
  const start = data[0].balance;
  const rest = data.slice(1);
  const inn = rest.reduce((sum, d) => sum + d.inn, 0);
  const ut = rest.reduce((sum, d) => sum + d.ut, 0);
  const end = rest.length ? rest[rest.length - 1].balance : start;
  const adj = end - (start + inn - ut);
  return <div className="aii-math">
    <div className="aii-math-row"><span data-help-title={t('adv.cf.mathStart')} data-help={t('adv.help.cashBalance')}>{t('adv.cf.mathStart')}</span><b>{fmt(start)}</b></div>
    <div className="aii-math-row plus"><span data-help-title={t('adv.cf.mathIn')} data-help={t('adv.help.bookedIn')}>{t('adv.cf.mathIn')}</span><b>+{fmt(inn)}</b></div>
    <div className="aii-math-row minus"><span>{t('adv.cf.mathOut')}</span><b>−{fmt(ut)}</b></div>
    {adj !== 0 && <div className="aii-math-row"><span>{t('adv.cf.mathAdjust')}</span><b>{adj > 0 ? '+' : '−'}{fmt(Math.abs(adj))}</b></div>}
    <div className="aii-math-row total"><span data-help-title={t('adv.cf.mathEnd')} data-help={t('adv.help.endBalance')}>{t('adv.cf.mathEnd')}</span><b>{fmt(end)}</b></div>
  </div>;
}

// Sier hva kurven faktisk er. Uten dette ser en flat linje etter dagens måned ut som
// en spådom om at ingenting kommer til å skje, i stedet for fravær av bokførte tall.
function AiCertaintyNote({ data }) {
  const counts = data.reduce((acc, d) => ({ ...acc, [d.certainty || 'confirmed']: (acc[d.certainty || 'confirmed'] || 0) + 1 }), {});
  const uncertain = counts.uncertain || 0;
  return <div className="aii-uncertainty">
    <div className="aii-uncertainty-legend">
      <span><span className="dot confirmed" />{t('adv.cf.certConfirmed')}</span>
      <span><span className="dot expected" />{t('adv.cf.certExpected')}</span>
      {(counts.planned || 0) > 0 && <span><span className="dot planned" />{t('adv.cf.certPlanned')}</span>}
      <span><span className="dot uncertain" />{t('adv.cf.certUncertain')}</span>
    </div>
    {uncertain > 0 && <p className="t-sm ink-2" style={{ margin: 0 }}>{t('adv.cf.certNote', { count: uncertain })}</p>}
  </div>;
}

function AiSecHead({ icon, title, sub, right, help }) {
  return <div className="row between" style={{ alignItems: 'flex-start', gap: 14, flexWrap: 'wrap', marginBottom: 4 }}>
    <div className="aii-sec-head" style={{ marginBottom: 0 }}>
      <span className="aii-sec-ico"><Icon name={icon} size={20} /></span>
      <div><div className="aii-sec-title" {...(help ? { 'data-help': help, 'data-help-title': title } : {})}>{title}</div>{sub && <div className="aii-sec-sub">{sub}</div>}</div>
    </div>
    {right}
  </div>;
}

// ---------- SuperPlumma chat (shared by AI konsulten + Privat økonomi) ----------
// Bygges ved render, ikke ved modullasting: t() på modulnivå ville frosset teksten
// til språket som var aktivt da fila ble evaluert.
const aiiChatConfig = (context) => context === 'privat'
  ? {
    intro: t('adv.chat.private.intro'),
    sugg: [t('adv.chat.private.sugg1'), t('adv.chat.private.sugg2'), t('adv.chat.private.sugg3'), t('adv.chat.private.sugg4')],
  }
  : {
    intro: t('adv.chat.business.intro'),
    sugg: [t('adv.chat.business.sugg1'), t('adv.chat.business.sugg2'), t('adv.chat.business.sugg3'), t('adv.chat.business.sugg4')],
  };
// NB: mønstrene under matcher norske nøkkelord. Engelsk UI faller derfor oftere til
// default-svaret i lokal fallback — backend-rådgiveren gjør den reelle tolkningen.
function aiiReply(q, context) {
  const s = (q || '').toLowerCase();
  if (context === 'privat') {
    if (/spar|kutt|abonnement|sav|subscription/.test(s)) return t('adv.reply.private.saving');
    if (/lån|refinansier|bolig|rente|loan|mortgage|interest/.test(s)) return t('adv.reply.private.loan');
    if (/fradrag|skatt|deduction|tax/.test(s)) return t('adv.reply.private.tax');
    if (/pensjon|pension/.test(s)) return t('adv.reply.private.pension');
    if (/score|helse|health/.test(s)) return t('adv.reply.private.score');
    return t('adv.reply.private.default');
  }
  if (/likvid|buffer|kontant|liquid|cash/.test(s)) return t('adv.reply.business.liquidity');
  if (/kostnad|kutt|spar|utgift|cost|spend/.test(s)) return t('adv.reply.business.costs');
  if (/skatt|mva|avgift|tax|vat/.test(s)) return t('adv.reply.business.tax');
  if (/score|helse|health/.test(s)) return t('adv.reply.business.score');
  if (/kunde|faktura|forfalt|customer|invoice|overdue/.test(s)) return t('adv.reply.business.invoices');
  return t('adv.reply.business.default');
}

// Samtalen skal henge sammen, men ikke vokse i det uendelige: hver melding sender med
// historikk, så et langt løp blir gradvis dyrere per svar. Grensen speiler PF_CHAT_MAX_TURNS
// i functions/personalFinance/personalFinancePolicy.ts. 0 = ingen grense.
const AII_CHAT_TURN_LIMIT = { privat: 12 };
const AII_PRIVATE_HISTORY_LIMIT = 8;
const aiiTurnLimit = (context) => AII_CHAT_TURN_LIMIT[context] || 0;
const aiiHistoryLimit = (context) => context === 'privat' ? AII_PRIVATE_HISTORY_LIMIT : 10;

function SuperPlummaAdvisorChat({ open, onClose, context = 'bedrift', ask, seed, setRoute }) {
  const cfg = aiiChatConfig(context);
  const [msgs, setMsgs] = aiUseState([{ r: 'a', t: cfg.intro }]);
  const [val, setVal] = aiUseState('');
  const [typing, setTyping] = aiUseState(false);
  // Pending-flyten fra plattform-routeren. Uten den mister et oppfolgingssporsmal
  // konteksten og godkjenningen starter forfra.
  const [pending, setPending] = aiUseState(null);
  const scroller = React.useRef(null);
  React.useEffect(() => { const el = scroller.current; if (el) el.scrollTop = el.scrollHeight; }, [msgs, typing, open]);
  React.useEffect(() => { if (open && seed) setVal(String(seed)); }, [open, seed]);
  const turnLimit = aiiTurnLimit(context);
  const historyLimit = aiiHistoryLimit(context);
  const turnsUsed = msgs.filter((message) => message.r === 'u').length;
  const limitReached = turnLimit > 0 && turnsUsed >= turnLimit;
  const startNewChat = () => { setMsgs([{ r: 'a', t: cfg.intro }]); setPending(null); setVal(''); };
  const send = async (preset) => {
    const q = (preset || val).trim(); if (!q || limitReached || typing) return;
    setMsgs((m) => [...m, { r: 'u', t: q }]); setVal(''); setTyping(true);
    try {
      const reply = ask
        ? await ask(q, {
          // Intro-meldingen er UI-tekst, ikke noe brukeren sa — den skal ikke koste tokens.
          history: msgs.slice(1).slice(-historyLimit).map((message) => ({ role: message.r === 'u' ? 'user' : 'assistant', content: message.t })),
          turnCount: turnsUsed + 1,
          pendingIntentName: pending?.intentName,
          pendingMissingFields: pending?.missingFields,
          pendingExecutionPayload: pending?.executionPayload,
        })
        : aiiReply(q, context);
      const actions = window.PlummeAssistantActions;
      setPending(actions ? actions.pendingFromReply(reply?.metadata) : null);
      const text = window.PlummeBackend?.formatAssistantReplyForChat?.(reply) || reply || aiiReply(q, context);
      setMsgs((m) => [...m, { r: 'a', t: text, action: actions ? actions.actionFromReply(reply?.metadata) : null }]);
    } catch (error) {
      // En avvist samtale skal ikke maskeres av lokal fallback — da ser det ut som
      // om SuperPlumma svarte, og brukeren skjønner ikke at hen må starte på nytt.
      if (error?.domainCode === 'assistant.conversationLimit') {
        setMsgs((m) => [...m, { r: 'a', t: t('adv.chat.limitReached', { max: turnLimit || turnsUsed + 1 }) }]);
      } else {
        setMsgs((m) => [...m, { r: 'a', t: context === 'bedrift' ? t('adv.chat.unavailable') : aiiReply(q, context) }]);
        if (window.toast) toast(error?.message || t('adv.chat.fallbackToast'), 'warning');
      }
    } finally {
      setTyping(false);
    }
  };
  if (!open) return null;
  const avatar = aiiPlumSrc();
  return <AiiChatModal open onClose={onClose}
    title={<span className="aii-chat-title"><span className="aii-chat-avatar"><img src={avatar} alt="" /></span>{t('aii.superPlumma')}</span>}
    footer={limitReached
      ? <div className="aii-chat-limit">
        <p>{t('adv.chat.limitReached', { max: turnLimit })}</p>
        <Button size="sm" icon="refresh" onClick={startNewChat}>{t('adv.chat.newChat')}</Button>
      </div>
      : <React.Fragment>
        {turnLimit > 0 && turnLimit - turnsUsed <= 3 && <div className="aii-chat-limit-hint">{t('adv.chat.limitNear', { left: turnLimit - turnsUsed })}</div>}
        <div className="aii-chat-input">
          <textarea className="input aii-chat-ta" rows={1} placeholder={t('adv.chat.placeholder')} value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
          <button className="aii-chat-send" onClick={() => send()} aria-label={t('adv.chat.send')}><Icon name="send" size={18} /></button>
        </div>
      </React.Fragment>}>
    <div className="aii-chat-msgs" ref={scroller}>
      {msgs.map((m, i) => m.r === 'a'
        ? <div key={i} className="aii-chat-row-a">
          <span className="aii-chat-avatar sm"><img src={avatar} alt="" /></span>
          <div style={{ minWidth: 0 }}>
            <div className="aii-chat-msg a">{m.t}</div>
            {/* Godkjenningen skjer i plattform-routerens egen flyt. Vi utfører ikke
                her — vi åpner riktig side med payloaden ferdig utfylt, akkurat som
                assistenten på Hjem gjør. */}
            {m.action && !m.actionDone && <div className="aii-chat-confirm">
              <div className="aii-chat-confirm-lbl"><Icon name="alertTri" size={14} />{t('adv.handoff.confirmLead')}</div>
              <div className="row gap-8" style={{ flexWrap: 'wrap' }}>
                <Button size="sm" iconRight="arrowRight" onClick={async () => {
                  try {
                    const outcome = await window.PlummeAssistantActions?.executeApprovedAction?.(m.action);
                    if (!outcome?.route) throw new Error(t('adv.handoff.noAdapter'));
                    setMsgs((list) => list.map((msg, idx) => idx === i ? { ...msg, actionDone: true } : msg));
                    setPending(null);
                    onClose();
                    setRoute ? setRoute(outcome.route) : window.plummeNav?.(outcome.route);
                  } catch (error) {
                    toast(error?.message || t('adv.handoff.noAdapter'), 'error');
                  }
                }}>{window.PlummeAssistantActions?.actionLabel(m.action)}</Button>
                <Button variant="ghost" size="sm" onClick={() => {
                  setMsgs((list) => list.map((msg, idx) => idx === i ? { ...msg, actionDone: true } : msg));
                  setPending(null);
                }}>{t('common.cancel')}</Button>
              </div>
            </div>}
          </div>
        </div>
        : <div key={i} className="aii-chat-msg u">{m.t}</div>)}
      {typing && <div className="aii-chat-row-a"><span className="aii-chat-avatar sm"><img src={avatar} alt="" /></span><div className="aii-chat-msg a aii-chat-typing"><span /><span /><span /></div></div>}
    </div>
    <div className="aii-chat-sugg">{cfg.sugg.map((s) => <button key={s} className="aii-chat-chip" onClick={() => send(s)}>{s}</button>)}</div>
  </AiiChatModal>;
}
function AiInnsiktPage({ setRoute, year = 2026 }) {
  const [horizon, setHorizon] = aiUseState(6);
  // `simulated` er et valgt tiltak fra rådgivermodellen, `scenarioIds` de hva-skjer-hvis
  // brukeren har slått på. Et tiltak og et scenario nullstiller hverandre — de er to ulike
  // forklaringer på den samme grønne linjen. Scenarioene seg imellom gjør det ikke: de
  // legges sammen, i den rekkefølgen de ble slått på.
  const [simulated, setSimulated] = aiUseState(null);
  const [scenarioIds, setScenarioIds] = aiUseState([]);
  // Inputene ligger per scenario, slik at ansatt-linjene og prisøkningen ikke deler de
  // samme feltene. Nøkkelen er scenario-id-en.
  const [scenarioInputs, setScenarioInputs] = aiUseState({});
  const [insightView, setInsightView] = aiUseState('rapport');
  const [reportMode, setReportMode] = aiUseState('mnd');
  const [statusTick, setStatusTick] = aiUseState(0);
  const [recommendationsModalOpen, setRecommendationsModalOpen] = aiUseState(false);
  const [chatOpen, setChatOpen] = aiUseState(false);
  const [hunterOpen, setHunterOpen] = aiUseState(false);
  const [tab, setTab] = aiUseState(() => {
    const returnTab = window.__plummeAdvisorReturnTab;
    window.__plummeAdvisorReturnTab = null;
    return returnTab === 'skatt' ? 'skatt' : 'oversikt';
  });
  const [taskCenterOpen, setTaskCenterOpen] = aiUseState(false);
  const [taskFlow, setTaskFlow] = aiUseState(null);
  const [reportOpen, setReportOpen] = aiUseState(false);
  const [reportRefresh, setReportRefresh] = aiUseState(0);
  const [analysisOpen, setAnalysisOpen] = aiUseState(false);
  const [monitorOpen, setMonitorOpen] = aiUseState(false);
  const [basisOpen, setBasisOpen] = aiUseState(false);
  const [industryIndex, setIndustryIndex] = aiUseState(null);
  const [serverAdvisor, setServerAdvisor] = aiUseState(null);
  const [serverAdvisorKey, setServerAdvisorKey] = aiUseState(null);
  const [advisorLoading, setAdvisorLoading] = aiUseState(false);
  const [generatedInsights, setGeneratedInsights] = aiUseState(null);
  const [generatedInsightsKey, setGeneratedInsightsKey] = aiUseState(null);
  const [generatedInsightsLoading, setGeneratedInsightsLoading] = aiUseState(false);
  const [generatedInsightsError, setGeneratedInsightsError] = aiUseState(null);
  const AII_TABS = [
    { value: 'oversikt', label: t('adv.tab.overview') },
    { value: 'likviditet', label: t('adv.tab.liquidity') },
    { value: 'salg', label: t('adv.tab.sales') },
    { value: 'kostnader', label: t('adv.tab.costs') },
    { value: 'budsjett', label: t('adv.tab.budget') },
    { value: 'skatt', label: t('adv.tab.tax') },
    { value: 'innsikt', label: t('adv.tab.insight') },
    { value: 'lagrede-rapporter', label: t('adv.rpt.saved.tab') },
  ];
  // Rådgivermodellen bygger hele årsrapporten på nytt. Uten memo skjer det på hver
  // render — også når det bare er en fane, en chatmelding eller et statusklikk som
  // endret seg. Deps er de samme som modellen faktisk leser: år, aktivt foretak og
  // state-objektet, som byttes ut i sin helhet ved hver oppdatering.
  const advisorRequestKey = `${window.PlummeBackend?.activeBusinessId || ''}:${year}`;
  React.useEffect(() => { setTaskFlow(null); setTaskCenterOpen(false); setAnalysisOpen(false); }, [advisorRequestKey]);
  const localAdvisor = React.useMemo(
    () => window.PlummeBackend?.getAiAdvisorModel?.({ year }) || null,
    [year, window.PlummeBackend?.activeBusinessId, window.PlummeBackend?.appState],
  );
  const currentServerAdvisor = serverAdvisorKey === advisorRequestKey && serverAdvisor?.sourceRefs?.modelVersion === '2026-09-22-advisor-review-1' ? serverAdvisor : null;
  const currentGeneratedInsights = generatedInsightsKey === advisorRequestKey ? generatedInsights : null;
  const advisor = currentServerAdvisor || localAdvisor;
  const monthlyGenerated = window.PlummeAdvisorInsightPresentation.select(currentGeneratedInsights?.monthly)
    || window.PlummeAdvisorInsightPresentation.select(currentGeneratedInsights?.daily);
  // Den lokale modellen er bygd fra cachet app-state og kan være eldre enn dagens
  // servergrunnlag. Morgenbriefen holdes derfor nøytral til både modellen og den
  // genererte innsikten er avklart for riktig foretak og år.
  const briefLoading = advisorLoading || generatedInsightsLoading
    || serverAdvisorKey !== advisorRequestKey || generatedInsightsKey !== advisorRequestKey;
  const hasAdvisor = Boolean(advisor);
  const score = advisor?.score ?? null;
  const categories = advisor?.categories || [];
  const recommendations = (globalThis.PlummeAiAdvisorTasks?.withAdvisorProfile || ((recs) => recs))((advisor?.recommendations || []).map((raw) => {
    const rec = globalThis.PlummeAiAdvisorTasks?.withTaskWhy?.(raw) || raw;
    return rec.textKey ? { ...rec, text: t(rec.textKey, rec.text, rec.vars) } : rec;
  }));
  // Keep observations in the advisor model; only concrete work enters today's list.
  const actionNeeded = globalThis.PlummeAiAdvisorStatus.dailyTasks(recommendations);
  const opportunities = recommendations.filter((r) => r.lvl === 'opp');
  const statuses = window.PlummeBackend?.getAdvisorRecommendationStatuses?.() || {};
  // Utfallsmålingen leser dagens verdier fra hjem-modellen og er derfor like tung som
  // rådgivermodellen. `statusTick` er med fordi det å merke et råd skal oppdatere den.
  const outcomes = React.useMemo(
    () => window.PlummeBackend?.getAiAdvisorOutcomesModel?.() || null,
    [statusTick, window.PlummeBackend?.activeBusinessId, window.PlummeBackend?.appState],
  );
  const statusOf = (rec) => globalThis.PlummeAiAdvisorStatus
    ? globalThis.PlummeAiAdvisorStatus.resolveStatus(statuses[rec.id], rec)
    : null;
  const setStatus = async (rec, patch) => {
    if (!window.PlummeBackend?.setAdvisorRecommendationStatus) return;
    try {
      await window.PlummeBackend.setAdvisorRecommendationStatus(rec.id, {
        ...patch,
        fingerprint: globalThis.PlummeAiAdvisorStatus?.fingerprintOf(rec),
        // Effekttype og anslag følger med, så utfallet senere kan sammenlignes med
        // det som faktisk sto på kortet da du merket det.
        effectType: rec.effectType,
        estimateLabel: rec.impact,
      });
      setStatusTick((n) => n + 1);
      return true;
    } catch (error) {
      toast(error?.message || t('adv.status.saveFailed'), 'error');
      return false;
    }
  };
  // Handlingssenteret grupperer på status, ikke på hvor viktig rådet er: det brukeren
  // trenger å vite er hva som fortsatt venter på hen.
  const buckets = globalThis.PlummeAiAdvisorStatus
    ? globalThis.PlummeAiAdvisorStatus.groupByBucket(actionNeeded, statuses)
    : { now: actionNeeded.map((rec) => ({ rec, resolved: null })), planned: [], closed: [] };
  const visibleTasks = globalThis.PlummeAiAdvisorStatus.visibleNow(buckets);
  const shownTasks = visibleTasks.slice(0, 3);
  const additionalTasks = visibleTasks.slice(3);
  const forecast = advisor?.forecast || [];
  const buffer = advisor?.buffer ?? null;
  const bufferInfo = advisor?.liquidityBuffer || (buffer > 0 ? {
    mode: 'automatic',
    amount: buffer,
    recommendedAmount: buffer,
    basis: { minimum: 50000 },
  } : null);
  const hero = advisor?.hero || null;
  // Older server responses may still treat a confirmed month as upcoming and
  // omit the shortfall. Use the matching local analysis for this one section.
  const serverLiquidityVerdict = currentServerAdvisor?.verdicts?.likviditet;
  const staleLiquidityVerdict = serverLiquidityVerdict?.key === 'liquidityBufferBreach'
    && (serverLiquidityVerdict.vars?.shortfall == null
      || forecast.some((point) => point.m === serverLiquidityVerdict.vars?.month && point.certainty === 'confirmed'));
  const liquidityNarrative = currentServerAdvisor && (staleLiquidityVerdict || !currentServerAdvisor.liquidityWatch)
    && localAdvisor?.verdicts ? localAdvisor : advisor;
  const verdicts = { ...(advisor?.verdicts || {}), likviditet: liquidityNarrative?.verdicts?.likviditet };
  const why = { ...(advisor?.why || {}), likviditet: liquidityNarrative?.why?.likviditet };
  const moves = { ...(advisor?.moves || {}), likviditet: liquidityNarrative?.moves?.likviditet };
  const confidence = advisor?.confidence || null;
  const monitor = advisor?.monitor || [];
  // Statuslinja i heroen er den samme vurderingen som helsekortet gir — den skal aldri
  // kunne si «god» mens kortet sier «svak».
  const healthMeta = aiHealthTone(score);
  // Notatet peker på den kategorien som faktisk trekker mest ned, i stedet for en
  // generisk formulering som ville stått uendret uansett tall.
  const healthNote = categories.length
    ? aiiHealthCategoryNote([...categories].filter((item) => item.pct != null).sort((a, b) => a.pct - b.pct)[0] || {}) || null
    : null;
  const userFirstName = window.PlummeBackend?.getAdvisorGreetingName?.() || null;
  // Dokumentasjonen kollapses først når det ligger analyse over den.
  // Kalenderen har ingen egen kilde i rådgivermodellen — fristene eies av hjem-modellen.
  // Locale er med i dependencies: månedsnavnene formatteres av PlummeI18n.
  const calendar = React.useMemo(
    () => {
      const home = window.PlummeBackend?.getHomeDashboardModel?.({ year });
      return buildAiCalendar(home?.deadlines, home?.publicFees);
    },
    [year, window.PlummeBackend?.activeBusinessId, window.PlummeBackend?.appState, PlummeI18n.getLocale()],
  );
  // Planlagte hendelser brukeren har registrert legges på kurven. Uten dem er den
  // bare løpende saldo av bokførte tall, og står flat etter dagens måned.
  const plannedForecast = React.useMemo(() => {
    const events = window.PlummeBackend?.getPlannedCashEvents?.() || [];
    if (!events.length || !globalThis.PlummeAiAdvisorPlan) return { forecast, applied: [] };
    return globalThis.PlummeAiAdvisorPlan.applyEventsToForecast({ forecast, events, year });
  }, [forecast, year, window.PlummeBackend?.appState]);
  // En prognose skal peke fremover. Vinduet er derfor de `horizon` månedene som
  // inneholder inneværende måned og mest mulig av det som ligger foran — i august ga
  // et vindu låst til januar en «prognose» som var over for lengst, og et scenario
  // som startet neste måned traff ingen søyle. For tidligere år starter det i januar.
  const cfStart = year === new Date().getFullYear()
    ? Math.max(0, Math.min(new Date().getMonth(), plannedForecast.forecast.length - horizon))
    : 0;
  const cfData = plannedForecast.forecast.slice(cfStart, cfStart + horizon);
  const totalInn = cfData.reduce((s, d) => s + d.inn, 0);
  const totalUt = cfData.reduce((s, d) => s + d.ut, 0);
  const endBalance = cfData[cfData.length - 1]?.balance || 0;
  // Bare tiltak som faktisk flytter kontanter kan simuleres. Å sette av mva flytter
  // penger mellom egne kontoer, og «bygg buffer» er et mål, ikke en handling — de
  // har derfor ingen `simulate` i modellen og får ingen knapp her.
  const simulatableMoves = (moves.likviditet || []).filter((move) => move.simulate && move.simulate.deltaBalance);
  const simulatedDelta = simulated == null ? 0 : (simulatableMoves[simulated]?.simulate.deltaBalance || 0);
  // Effekten legges på fra måned to og utover: den første søylen er saldoen i dag,
  // og den endrer seg ikke av et tiltak du ennå ikke har gjort.
  const moveAfter = simulated == null || !cfData.length ? null
    : cfData.map((d, i) => i === 0 ? d : { ...d, balance: Math.max(0, d.balance + simulatedDelta) });
  // Scenarioene bygges av det samme datagrunnlaget som kurven, og satsene hentes fra
  // foretakets egne lønnsinnstillinger i stedet for å hardkodes.
  const payrollRates = window.PlummeBackend?.getAdvisorPayrollRates?.() || null;
  // Kundekonsentrasjonen leses av modellens egne partsinnsikter. `shareBasis` må være
  // årsinntekt — en andel målt mot noe annet ville ikke svart på hva største kunde er
  // verdt per måned.
  const topCustomer = advisor?.partyInsights?.customers?.[0];
  const customerConcentration = topCustomer?.shareBasis === 'yearIncome' && Number.isFinite(Number(topCustomer.sharePct))
    ? Number(topCustomer.sharePct)
    : null;
  const advisorCountry = String(window.PlummeBackend?.appState?.settings?.country || '').toUpperCase();
  const fixedCostBasis = React.useMemo(() => {
    const api = globalThis.PlummeAiAdvisorScenarios;
    const currentMonth = new Date().getMonth();
    if (advisorCountry !== 'NO' || year !== new Date().getFullYear() || !api?.buildFixedCostBasis) return null;
    const costLines = (reportYear, months) => window.PlummeBackend?.getReportsModel?.({ year: reportYear, months, includeChart: false, includeTrial: false })
      ?.result?.sections?.find((section) => section.key === 'kostnader')?.lines || [];
    const currentLines = costLines(year, [0, currentMonth]);
    const previousLines = currentMonth < 11 ? costLines(year - 1, [currentMonth + 1, 11]) : [];
    return api.buildFixedCostBasis({ country: advisorCountry, year, currentYear: new Date().getFullYear(), currentMonth, currentLines, previousLines });
  }, [advisorCountry, year, window.PlummeBackend?.activeBusinessId, window.PlummeBackend?.appState]);
  const scenarioModel = React.useMemo(
    () => globalThis.PlummeAiAdvisorScenarios?.buildScenarios?.({
      forecast: plannedForecast.forecast,
      concentration: customerConcentration,
      employerRate: payrollRates?.employerContributionRate,
      holidayRate: payrollRates?.holidayPayPercent,
      fixedCostBasis,
      year,
    }) || { scenarios: [], monthlyIncome: 0 },
    [plannedForecast.forecast, customerConcentration, fixedCostBasis, year, payrollRates?.employerContributionRate, payrollRates?.holidayPayPercent],
  );
  // Avledningen av aktive scenarioer bor ved siden av kortet som viser dem. Siden eier
  // valget og linjene; visningsmodellen — beregning, kurve, tone og samletekst — er
  // `buildAiiScenarioView`.
  const scenarioApi = globalThis.PlummeAiAdvisorScenarios || null;
  const scenarioView = buildAiiScenarioView({
    model: scenarioModel,
    scenarioIds,
    scenarioInputs,
    cfData,
    cfStart,
    buffer,
    year,
    months: plannedForecast.forecast.length,
  });
  const scenarioEntries = scenarioView.entries;
  const scenarioPctFor = scenarioView.pctFor;
  const scenarioAfter = scenarioView.after;
  // Ett tiltak eller scenarioene — kurven kan bare ha én forklaring om gangen.
  const cfAfter = scenarioAfter || moveAfter;
  const cfAfterLabel = scenarioAfter ? scenarioView.label : t('adv.cf.simAfter');
  const forecastComparison = cfAfter ? scenarioApi?.compareForecasts?.({ base: cfData, after: cfAfter, buffer }) : null;

  // Skatterådene trenger både hjem-modellen (mva, skattebuffer, manglende bilag) og
  // likviditetsprognosen, fordi anbefalingen om å utsette et kjøp avhenger av om
  // bufferen tåler uttaket i de månedene som er igjen av året.
  const taxAdvice = React.useMemo(() => {
    const home = window.PlummeBackend?.getHomeDashboardModel?.({ year });
    if (!home || !globalThis.PlummeAiAdvisorTax) return null;
    const settings = window.PlummeBackend?.appState?.settings || {};
    const form = String(settings.companyTypeCode || '').toUpperCase();
    return globalThis.PlummeAiAdvisorTax.buildTaxAdvice({
      settings,
      home,
      plannedEvents: window.PlummeBackend?.getPlannedCashEvents?.() || [],
      forecast: plannedForecast.forecast,
      buffer,
      monthIndex: year === new Date().getFullYear() ? new Date().getMonth() : 0,
      investmentIncluded: true,
      taxModel: settings.country === 'NO' && form === 'AS' ? window.PlummeBackend?.getTaxReturnModel?.({ year }) : null,
      taxPrepayments: window.PlummeBackend?.appState?.taxPrepaymentsByYear?.[String(year)] || [],
      year,
    });
  }, [year, buffer, plannedForecast.forecast, window.PlummeBackend?.appState, window.PlummeBackend?.activeBusinessId]);
  // Bransjegrunnlaget. Bransjekoden kommer fra Enhetsregisteret ved verifisert claim.
  // Selve indeksen bygges av en planlagt jobb og hentes asynkront — er den ikke bygget
  // for denne bransjen og størrelsesgruppen, sier grunnlaget hvorfor.
  const industryBasis = React.useMemo(
    () => globalThis.PlummeAiAdvisorIndustry?.buildIndustryBasis?.({
      settings: window.PlummeBackend?.appState?.settings || {},
      business: window.PlummeBackend?.getActiveBusinessProfile?.() || {},
      year,
      employeeCount: (window.PlummeBackend?.appState?.employees || []).length,
      index: industryIndex,
      financials: advisor?.financials,
      bench: advisor?.bench,
      employees: advisor?.employees,
    }) || null,
    [year, industryIndex, advisor, window.PlummeBackend?.appState, window.PlummeBackend?.activeBusinessId],
  );
  const selectMove = (index) => { setScenarioIds([]); setSimulated(index); };
  // Å skru av et scenario beholder det brukeren har skrevet. Slår man det på igjen for å
  // sammenligne, står linjene der de sto — de ville ellers vært tapt ved et feilklikk.
  const toggleScenario = (id) => {
    setSimulated(null);
    setScenarioIds((current) => (current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id]));
  };
  const setScenarioInput = (id, key, value) => setScenarioInputs((current) => ({
    ...current,
    [id]: { ...(current[id] || {}), [key]: value },
  }));
  // Linjene redigeres alltid mot den listen beregningen ser. Første endring på et
  // uendret scenario materialiserer standardlinjen — derfor går alt via `rowsOf`.
  const updateScenarioRows = (id, transform) => setScenarioInputs((current) => {
    const scenario = scenarioModel.scenarios.find((entry) => entry.id === id);
    const inputs = current[id] || {};
    return { ...current, [id]: { ...inputs, rows: transform(scenarioApi?.rowsOf?.(scenario, inputs) || []) } };
  });
  const changeScenarioRow = (id, index, key, value) => updateScenarioRows(id, (rows) =>
    rows.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
  const addScenarioRow = (id) => updateScenarioRows(id, (rows) =>
    [...rows, scenarioApi?.defaultRow?.(scenarioModel.scenarios.find((entry) => entry.id === id)) || {}]);
  const removeScenarioRow = (id, index) => updateScenarioRows(id, (rows) =>
    (rows.length > 1 ? rows.filter((_, i) => i !== index) : rows));
  const askAdvisor = (question, options = {}) => window.PlummeBackend?.askAiAdvisorAssistant
    ? window.PlummeBackend.askAiAdvisorAssistant(question, { ...options, year })
    : null;
  const askAdvisorControl = (question, options = {}) => askAiAdvisorThroughPageControl({ question, options, fallback: (q, opts) => { setChatOpen(true); return askAdvisor(q, opts); }, translate: t, beforeAsk: () => ReactDOM.flushSync(() => setChatOpen(false)) });
  useAiAdvisorPageControlAdapter({ year, tab, setTab, horizon, setHorizon, cfData, endBalance, scenarioAfter, buffer, scenarioModel, scenarioApi, scenarioIds, setScenarioIds, scenarioInputs, setScenarioInputs, setSimulated });
  const loadGeneratedInsights = async (forceKind = null) => {
    if (!window.PlummeBackend?.fetchAiAdvisorInsights) {
      setGeneratedInsights(null);
      setGeneratedInsightsKey(advisorRequestKey);
      return;
    }
    setGeneratedInsightsLoading(true);
    setGeneratedInsightsError(null);
    try {
      const next = await window.PlummeBackend.fetchAiAdvisorInsights({
        year,
        forceDaily: forceKind === 'daily',
        forceMonthly: forceKind === 'monthly',
        forcePreviousMonthly: forceKind === 'previousMonthly',
      });
      setGeneratedInsights(next || null);
      if (next) {
        if (forceKind && next?.[forceKind]?.refreshSkipped && window.toast) {
          const messageKey = window.PlummeAdvisorInsightPresentation.notice(next[forceKind], forceKind);
          toast(t(messageKey), 'info');
        }
      }
      setGeneratedInsightsKey(advisorRequestKey);
    } catch (error) {
      setGeneratedInsightsError(error);
      setGeneratedInsightsKey(advisorRequestKey);
      if (forceKind && window.toast) toast(error?.message || t('adv.generated.failed'), 'warning');
    } finally {
      setGeneratedInsightsLoading(false);
    }
  };
  const refreshAdvisorAfterBufferSave = async () => {
    // Slipp en eventuell eldre servermodell med en gang, slik at lokalmodellen viser
    // den lagrede verdien mens den nye backendmodellen hentes.
    setServerAdvisor(null);
    if (window.PlummeBackend?.fetchAiAdvisorModel) {
      try {
        const model = await window.PlummeBackend.fetchAiAdvisorModel({ year });
        setServerAdvisor(model?.liquidityBuffer ? model : null);
      } catch {
        setServerAdvisor(null);
      }
    }
    // Vanlig cachekontroll bruker ikke nye AI-tokens samme dag, men markerer at
    // dagsinnsikten kan oppdateres fordi analysegrunnlaget er endret.
    await loadGeneratedInsights();
  };
  const go = (route) => route === 'aiinsikt' ? setTab('likviditet') : route ? (setRoute && setRoute(route)) : null;
  const openMissingVouchers = () => { window.__plummeVoucherFilter = { checks: { kjop: true, manglervedlegg: true } }; setRoute && setRoute('allebilag'); };
  const openRecommendation = (rec) => setTaskFlow(rec);
  // Rapporter og Bransje er slått sammen til én fane. Aliasene holder eldre
  // handlinger som peker på de gamle fanenavnene i live.
  const AII_TAB_ALIAS = { rapporter: 'innsikt', bransje: 'innsikt' };
  const act = (action) => {
    if (!action) return;
    if (action.key === 'moveFixVouchers') return openMissingVouchers();
    if (action.route) return setRoute && setRoute(action.route);
    if (action.tab) {
      const next = AII_TAB_ALIAS[action.tab] || action.tab;
      if (next === 'innsikt') setInsightView(action.tab === 'bransje' ? 'bransje' : 'rapport');
      setTab(next);
    }
  };
  // Bransjeindeksen hentes bare når vi faktisk har en bransjekode å slå opp på, og
  // bare når Innsikt-fanen er i bruk — den er ikke relevant for de andre fanene.
  React.useEffect(() => {
    let alive = true;
    const business = window.PlummeBackend?.getActiveBusinessProfile?.() || {};
    const industryCode = business.industryCode || window.PlummeBackend?.appState?.settings?.industryCode || '';
    const sizeBand = globalThis.PlummeAiAdvisorIndustry?.sizeBandFor?.((window.PlummeBackend?.appState?.employees || []).length);
    if (tab !== 'innsikt' || !industryCode || !sizeBand) return undefined;
    void (async () => {
      const next = await window.PlummeBackend?.fetchIndustryIndex?.({ industryCode, sizeBand });
      if (alive) setIndustryIndex(next || null);
    })();
    return () => { alive = false; };
  }, [tab, window.PlummeBackend?.activeBusinessId, window.PlummeBackend?.appState]);
  React.useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!window.PlummeBackend?.fetchAiAdvisorModel) {
        setServerAdvisor(null);
        setServerAdvisorKey(advisorRequestKey);
        return;
      }
      setAdvisorLoading(true);
      try {
        const model = await window.PlummeBackend.fetchAiAdvisorModel({ year });
        if (alive) {
          setServerAdvisor(model?.liquidityBuffer ? model : null);
          setServerAdvisorKey(advisorRequestKey);
        }
      } catch (error) {
        if (alive) {
          setServerAdvisor(null);
          setServerAdvisorKey(advisorRequestKey);
        }
      } finally {
        if (alive) setAdvisorLoading(false);
      }
    };
    void load();
    return () => { alive = false; };
  }, [year, window.PlummeBackend?.activeBusinessId]);
  React.useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!window.PlummeBackend?.fetchAiAdvisorInsights) {
        setGeneratedInsights(null);
        setGeneratedInsightsKey(advisorRequestKey);
        return;
      }
      setGeneratedInsightsLoading(true);
      setGeneratedInsightsError(null);
      try {
        const next = await window.PlummeBackend.fetchAiAdvisorInsights({ year });
        if (alive) {
          setGeneratedInsights(next || null);
          setGeneratedInsightsKey(advisorRequestKey);
        }
      } catch (error) {
        if (alive) {
          setGeneratedInsights(null);
          setGeneratedInsightsKey(advisorRequestKey);
          setGeneratedInsightsError(error);
        }
      } finally {
        if (alive) setGeneratedInsightsLoading(false);
      }
    };
    void load();
    return () => { alive = false; };
  }, [year, window.PlummeBackend?.activeBusinessId]);

  return <div className="aii-page">
    {/* header */}
    <div className="aii-head" data-help={t('aiHelp.advisor.page.body')} data-help-title={t('aiHelp.advisor.page.title')}>
      <div className="aii-eyebrow">{t('adv.eyebrow')}</div>
      <div className="row gap-12" style={{ alignItems: 'center', marginTop: 5 }}>
        <h2 className="t-h1">{t('nav.aiAdvisor')}</h2>
        <span className="aii-beta">{t('adv.beta')}</span>
        {advisorLoading && <span className="aii-beta">{t('adv.updating')}</span>}
        {hasAdvisor && window.AiiAdvisorProfileButton && <window.AiiAdvisorProfileButton
          forecast={forecast}
          bufferControl={bufferInfo && window.AiLiquidityBufferControl
            ? <window.AiLiquidityBufferControl bufferInfo={bufferInfo} onSaved={refreshAdvisorAfterBufferSave} />
            : null}
        />}
      </div>
      <div className="t-sm muted" style={{ marginTop: 4, fontWeight: 600 }}>{t('adv.lead', { year })}</div>
    </div>

    {/* morgenbrief — showpiece */}
    {window.AiiAdvisorHero && <window.AiiAdvisorHero
      compact={tab !== 'oversikt'} year={year} generatedAt={monthlyGenerated?.generatedAt} analysisStale={monthlyGenerated?.refreshAvailable}
      loading={briefLoading}
      overnight={advisor?.overnight || null}
      effects={advisor?.heroEffects || []}
      healthTone={healthMeta.tone}
      healthWord={hasAdvisor ? healthMeta.word : null}
      healthNote={score == null ? null : healthNote}
      leadText={monthlyGenerated?.summary || (visibleTasks.length ? t('adv.hero.priorityLead', { task: visibleTasks[0].rec.title || visibleTasks[0].rec.text }) : t('adv.hero.noOpenTasks'))}
      userName={userFirstName}
      taskCount={visibleTasks.length}
      onOpenTasks={() => setTaskCenterOpen(true)}
      onOpenAnalysis={() => setAnalysisOpen(true)}
      analysisAvailable={Boolean(window.AiGeneratedInsightPanel)}
      onAsk={() => setChatOpen(true)}
      onCreateReport={window.AiiReportModal ? () => setReportOpen(true) : null}
      plumSrc={aiiPlumSrc()}
    />}

    {/* tabs */}
    <div className="aii-tabs" data-help={t('aiHelp.advisor.tabs.body')} data-help-title={t('aiHelp.advisor.tabs.title')}><Tabs options={AII_TABS} value={tab} onChange={setTab} /></div>

    {tab === 'oversikt' && <React.Fragment>
    {/* Den genererte analysen ligger bak «Se analyse» i heroen, ikke som et eget kort
        her: den er utdypning, ikke det første du skal lese. */}
    {/* economic health score */}
    <section className="aii-health-section aii-section-frame">
      <AiSecHead icon="checkCircle" title={t('adv.health.title')} sub={t('adv.health.sub')} help={t('adv.health.help')} />
    <Card>
      <div className="aii-score-grid">
        <AiHealthCard score={score} />
        <div style={{ width: '100%' }}>
          {categories.length ? <div className="aii-cats">
            {categories.map((c) => <div key={c.key} data-help={t('aiHelp.advisor.healthCategory.body')} data-help-title={aiiHealthCategoryName(c)}>
              <div className="aii-cat-top">
                <span className="aii-cat-dot" style={{ background: AII_STATUS_COLOR[c.status] }} />
                <span className="aii-cat-name">{aiiHealthCategoryName(c)}</span>
                <span className="aii-cat-val" style={{ color: AII_STATUS_COLOR[c.status] }}>{aiiHealthCategoryVal(c)}</span>
              </div>
              <div className="aii-cat-track"><div className="aii-cat-fill" style={{ width: (c.pct * 100) + '%', background: AII_STATUS_COLOR[c.status] }} /></div>
              <div className="aii-cat-note">{aiiHealthCategoryNote(c)}</div>
            </div>)}
          </div> : <EmptyState noVisual acc="sparkle" title={t('adv.health.emptyTitle')} text={t('adv.health.emptyText')} compact />}
        </div>
      </div>
      <div className="aii-summary" data-help={t('aiHelp.advisor.summary.body')} data-help-title={t('aiHelp.advisor.summary.title')}>
        <span className="aii-summary-ico"><Icon name="sparkle" size={18} /></span>
        <p>{score == null ? t('adv.health.preliminary') : advisor?.summaryText || t('adv.health.summaryFallback')}</p>
      </div>
    </Card>
    </section>
    {/* recommendations */}
    <div className="aii-section-frame">
      <AiSecHead icon="sparkle" title={t('adv.prio.title')} sub={t('adv.prio.sub')} help={t('adv.prio.help')}
        right={additionalTasks.length > 0 ? <Button type="button" variant="ghost" size="sm" iconRight="chevRight"
          aria-haspopup="dialog" onClick={() => setRecommendationsModalOpen(true)}>
          {t('adv.prio.showMore', { count: additionalTasks.length })}
        </Button> : null} />
      {shownTasks.length > 0 && <div id="aii-priority-list" className="aii-prio-grid" style={{ marginTop: 14 }}>
        {shownTasks.map(({ rec, resolved }, i) => <AiPrioCard key={rec.id || i} p={rec} rank={i + 1}
          onAct={() => openRecommendation(rec)} resolved={resolved} onSetStatus={(patch) => setStatus(rec, patch)} />)}
      </div>}
      {recommendationsModalOpen && <Modal open onClose={() => setRecommendationsModalOpen(false)} title={t('adv.prio.moreTitle')} max={1440} center portal className="aii-prio-more-modal"
        footer={<Button onClick={() => setRecommendationsModalOpen(false)}>{t('common.close')}</Button>}>
        <div className="aii-prio-grid">
          {additionalTasks.map(({ rec, resolved }, i) => <AiPrioCard key={rec.id || i} p={rec} rank={i + 4}
            onAct={() => { setRecommendationsModalOpen(false); openRecommendation(rec); }} resolved={resolved} onSetStatus={(patch) => setStatus(rec, patch)} />)}
        </div>
      </Modal>}
      {buckets.now.length === 0 && actionNeeded.length > 0 && <Card className="mt-16">
        <EmptyState noVisual acc="check" title={t('adv.status.allHandledTitle')} text={t('adv.status.allHandledText')} compact />
      </Card>}
      {actionNeeded.length === 0 && <Card className="mt-16">
        <EmptyState noVisual acc="check" title={t('adv.prio.emptyTitle')} text={t('adv.prio.emptyText')} compact />
      </Card>}

      {/* Utsatte og lukkede råd forsvinner ikke — de flyttes ned, så du kan finne
          tilbake til noe du utsatte og angre på at du avviste. */}
      {(buckets.planned.length > 0 || buckets.closed.length > 0) && <div className="aii-taskgroups">
        {[['planned', buckets.planned], ['closed', buckets.closed]].filter(([, rows]) => rows.length).map(([bucket, rows]) => <div key={bucket} className="aii-taskgroup">
          <div className="aii-taskgroup-lbl">{t('adv.status.bucket.' + bucket)}<span>{rows.length}</span></div>
          <div className="aii-taskgroup-rows">
            {rows.map(({ rec, resolved }) => <div key={rec.id} className="aii-task-row">
              <span className="aii-task-ico"><Icon name={rec.icon} size={16} /></span>
              <span className="aii-task-body">
                <span className="aii-task-title">{rec.title || rec.text}</span>
                {resolved.note && <span className="aii-task-sub">{resolved.note}</span>}
              </span>
              <AiiStatusPill resolved={resolved} />
              <AiiStatusMenu rec={rec} resolved={resolved} onSet={(patch) => setStatus(rec, patch)} />
            </div>)}
          </div>
        </div>)}
      </div>}
    </div>

    {/* The three highest-ranked opportunities appear before the calendar. */}
    {window.AiiOpportunitiesSection && <window.AiiOpportunitiesSection opportunities={opportunities} onGo={go} />}

    {window.AiiCalendarSection && <window.AiiCalendarSection calendar={calendar} onNavigate={setRoute} />}

    <div className="aii-overview-disclosures">
    {monitor.length > 0 && <Card className="aii-monitor-card">
      <AiSecHead icon="sparkle" title={t('adv.monitor.title')} sub={t('adv.monitor.sub')} help={t('adv.monitor.help')}
        right={<button type="button" className="aii-monitor-toggle" onClick={() => setMonitorOpen(true)} aria-haspopup="dialog">
          <span>{t('adv.basis.expand')}</span>
          <Icon name="chevRight" size={16} />
        </button>} />
    </Card>}

    {confidence && <Card className="aii-basis-card">
      <AiSecHead icon="database" title={t('adv.basis.title')} sub={t('adv.basis.sub')} help={t('adv.basis.help')}
        right={<button type="button" className="aii-basis-toggle" onClick={() => setBasisOpen(true)} aria-haspopup="dialog">
          <span className={'aii-basis-level tone-' + (confidence.level === 'good' ? 'green' : confidence.level === 'medium' ? 'amber' : 'muted')}>
            {t('adv.basis.' + (confidence.level || 'thin'))}
          </span>
          <span>{t('adv.basis.expand')}</span>
          <Icon name="chevRight" size={16} />
        </button>} />
    </Card>}
    </div>

    {monitorOpen && <Modal open onClose={() => setMonitorOpen(false)} title={t('adv.monitor.title')} max={820} center portal className="aii-overview-detail-modal">
      <div className="aii-monitor">
        {monitor.map((item) => {
          const vars = { ...(item.vars || {}) };
          if (Number.isFinite(Number(vars.amount))) vars.amount = fmt(Number(vars.amount));
          const structured = Boolean(item.detailKey);
          return <div key={item.key} className={'aii-monitor-item ' + item.status}>
            <span className="aii-monitor-ico"><Icon name={AII_MONITOR_ICON[item.key] || (item.status === 'ok' ? 'check' : 'alertTri')} size={16} /></span>
            <span className="aii-monitor-copy">
              <span className="aii-monitor-name">{t(structured ? 'adv.monitor.group.' + item.key : 'adv.monitor.' + item.key)}</span>
              {structured && <span className="aii-monitor-detail">{t('adv.monitor.detail.' + item.detailKey, vars)}</span>}
            </span>
          </div>;
        })}
      </div>
    </Modal>}

    {/* datagrunnlag — hva analysen bygger på og hva som mangler.
        Tidligere sto det en sikkerhetsprosent her. Den ga en presisjon modellen ikke
        har dekning for: den ser bare det som er bokført i Plumme. Nivået sier det
        samme ærligere, og manglene er klikkbare så de faktisk kan fylles inn. */}
    {basisOpen && confidence && <Modal open onClose={() => setBasisOpen(false)} title={t('adv.basis.title')} max={720} center portal className="aii-overview-detail-modal">
      <div className="aii-basis">
        <div className="aii-basis-cols">
          <div className="aii-basis-col">
            <div className="aii-conf-lbl" data-help-title={t('adv.conf.buildsOn')} data-help={t('adv.help.buildsOn')}>{t('adv.conf.buildsOn')}</div>
            {confidence.has?.length
              ? confidence.has.map((key) => <div key={key} className="aii-basis-row on"><Icon name="check" size={14} />{t('adv.conf.' + key)}</div>)
              : <div className="aii-basis-row off">{t('adv.conf.nothingYet')}</div>}
          </div>
          <div className="aii-basis-col">
            <div className="aii-conf-lbl" data-help-title={t('adv.conf.missing')} data-help={t('adv.help.missingData')}>{t('adv.conf.missing')}</div>
            {confidence.missing?.length
              ? confidence.missing.map((key) => {
                const route = AII_BASIS_ROUTE[key];
                if (!route) return <div key={key} className="aii-basis-row off"><Icon name="plus" size={14} />{t('adv.conf.' + key)}</div>;
                return <button key={key} type="button" className="aii-basis-row off" onClick={() => { setBasisOpen(false); if (setRoute) setRoute(route); }}>
                  <Icon name="plus" size={14} />{t('adv.conf.' + key)}<Icon name="chevRight" size={13} className="aii-basis-chev" />
                </button>;
              })
              : <div className="aii-basis-row on"><Icon name="check" size={14} />{t('adv.conf.nothingMissing')}</div>}
          </div>
        </div>
      </div>
    </Modal>}

    {/* Betingede moduler er datastyrte: uten lønnsdata tegnes de ikke. */}
    {window.AiiEmployeesModule && <window.AiiEmployeesModule employees={advisor?.employees || null} onNav={(route) => setRoute && setRoute(route)} />}

    {/* Utfall og læring — hva rådene faktisk førte til. Bak «Vis dokumentasjon»
        fordi det er etterprøving, ikke noe som haster. */}
    {outcomes && outcomes.counts.total > 0 && <AiDocs label={t('adv.outcome.title')}>
      <Card>
        <AiSecHead icon="checkCircle" title={t('adv.outcome.title')} sub={t('adv.outcome.sub')} help={t('adv.outcome.help')} />
        <div className="aii-outcome-kpis" style={{ marginTop: 12 }}>
          <div><span className="lbl">{t('adv.outcome.marked')}</span><b>{outcomes.counts.total}</b></div>
          <div><span className="lbl">{t('adv.outcome.wentRight')}</span><b className="good">{outcomes.counts.hits}</b></div>
          <div><span className="lbl">{t('adv.outcome.didNot')}</span><b>{outcomes.counts.misses}</b></div>
          <div><span className="lbl">{t('adv.outcome.notMeasurable')}</span><b>{outcomes.counts.unmeasurable}</b></div>
        </div>
        {/* Effektene står hver for seg, aldri summert til ett tall — samme regel
            som på anbefalingskortene. */}
        {outcomes.byEffect.length > 0 && <div className="aii-outcome-effects">
          {outcomes.byEffect.map((entry) => <div key={entry.effectType} className="aii-outcome-effect" data-help-title={entry.effectType === 'other' ? t('adv.outcome.otherEffect') : t('adv.effect.' + entry.effectType)} data-help={t(entry.effectType === 'other' ? 'adv.help.effect.other' : 'adv.help.effect.' + entry.effectType)}>
            <span className="lbl">{entry.effectType === 'other' ? t('adv.outcome.otherEffect') : t('adv.effect.' + entry.effectType)}</span>
            <b style={{ color: entry.improvement > 0 ? 'var(--success)' : entry.improvement < 0 ? 'var(--error)' : 'var(--muted)' }}>
              {entry.improvement > 0 ? '+' : entry.improvement < 0 ? '−' : ''}{fmt(Math.abs(entry.improvement))}
            </b>
            <span className="note">{t('adv.outcome.fromCount', { count: entry.count })}</span>
          </div>)}
        </div>}
        <div className="aii-outcome-list">
          {outcomes.outcomes.map((outcome) => <div key={outcome.id} className={'aii-outcome-row' + (outcome.measurable && outcome.result === 'none' ? ' miss' : '')}>
            <span className="aii-outcome-ico"><Icon name={!outcome.measurable ? 'info' : outcome.result === 'none' ? 'alertCircle' : 'checkCircle'} size={16} /></span>
            <div className="aii-outcome-body">
              <div className="aii-outcome-title">{t('adv.rec.' + outcome.recId, {}) || outcome.recId}</div>
              <div className="aii-outcome-nums">
                <span>{t('adv.outcome.estimated')}: <b>{outcome.estimateLabel || '—'}</b></span>
                {outcome.measurable
                  ? <span>{t('adv.outcome.actual')}: <b>{outcome.improvement > 0 ? '+' : outcome.improvement < 0 ? '−' : ''}{fmt(Math.abs(outcome.improvement))}</b></span>
                  : <span className="muted">{t('adv.outcome.reason.' + outcome.reason)}</span>}
              </div>
            </div>
          </div>)}
        </div>
        <p className="t-xs muted" style={{ marginTop: 10 }}>{t('adv.outcome.note')}</p>
      </Card>
    </AiDocs>}

    </React.Fragment>}

    {tab === 'likviditet' && <React.Fragment>
    <AiVerdict verdict={verdicts.likviditet} className="aii-verdict-liquidity" />
    {window.AiiLiquidityMoves && <window.AiiLiquidityMoves moves={moves.likviditet} onAct={act} />}
    {/* Tiltak og scenarioer bruker samme prognosekurve. */}
    {window.AiiScenarioCard && <window.AiiScenarioCard
      scenarios={scenarioModel.scenarios}
      fixedCostCountry={advisorCountry}
      fixedCostEligibleYear={year === new Date().getFullYear()}
      moves={simulatableMoves}
      selectedMove={simulated}
      onSelectMove={selectMove}
      moveLabelFor={(move) => t('adv.moves.' + move.key, aiiVars(move.vars))}
      comparison={forecastComparison}
      activeIds={scenarioIds}
      entries={scenarioEntries}
      pctFor={scenarioPctFor}
      onToggle={toggleScenario}
      onInput={setScenarioInput}
      onRowChange={changeScenarioRow}
      onAddRow={addScenarioRow}
      onRemoveRow={removeScenarioRow}
    />}
    {/* cashflow forecast */}
    <section className="aii-cf-section aii-section-frame">
      <div className="aii-cf-section-head">
        <AiSecHead icon="trendUp" title={t('adv.cf.title')} sub={t('adv.cf.sub')}
          help={t('adv.cf.help')}
          right={<div className="aii-cf-head-actions" data-help={t('aiHelp.advisor.forecastHorizon.body')} data-help-title={t('aiHelp.advisor.forecastHorizon.title')}>
            <Segmented value={horizon} onChange={setHorizon} options={[3, 6, 12].map((count) => ({ value: count, label: t('adv.cf.months', { count }) }))} />
          </div>} />
      </div>
    <Card>
      <p className="aii-generated-coverage">{t('adv.cf.basisNote')}</p>
      <div className="aii-cf-kpis" style={{ marginTop: 18 }}>
        <KPI tint="plum" icon="wallet" label={t('adv.cf.buffer')} value={hero?.balance != null ? fmt(hero.balance) : t('adv.cf.notCalculated')} help={t('adv.help.liquidity')} />
        <KPI tint="white" icon="trendUp" label={t('adv.cf.expectedBalance', { month: cfData[cfData.length - 1]?.m || '' })} value={cfData.length ? fmt(endBalance) : t('adv.cf.notCalculated')} help={t('adv.help.expectedBalance')} />
        <KPI tint="green" icon="arrowRight" label={t('adv.cf.expectedIn')} value={cfData.length ? fmt(totalInn) : t('adv.cf.notCalculated')} help={t('adv.help.expectedIn')} />
        <KPI tint="peach" icon="arrowRight" label={t('adv.cf.expectedOut')} value={cfData.length ? fmt(totalUt) : t('adv.cf.notCalculated')} help={t('adv.help.expectedOut')} />
      </div>
      {cfData.length
        ? <div style={{ aspectRatio: '2.6 / 1', overflow: 'hidden' }}><AiCashflowChart data={cfData} buffer={buffer} dataAfter={cfAfter} /></div>
        : <EmptyState noVisual acc="chart" title={t('adv.cf.emptyTitle')} text={t('adv.cf.emptyText')} compact />}
      <div className="aii-cf-legend" style={{ marginTop: 14 }}>
        <span className="aii-cf-leg"><span className="aii-cf-swatch" style={{ background: cfAfter ? 'var(--border-strong)' : 'var(--plum-600)' }} />{cfAfter ? t('adv.cf.simBase') : t('adv.cf.legendBalance')}</span>
        {cfAfter && <span className="aii-cf-leg"><span className="aii-cf-swatch" style={{ background: 'var(--success)' }} />{cfAfterLabel}</span>}
        {buffer > 0 && <span className="aii-cf-leg" data-help={t('helpTopics.buffer.body')} data-help-title={t('helpTopics.buffer.title')}><span className="aii-cf-swatch" style={{ background: 'var(--warning)' }} />{t('adv.cf.legendBuffer')}</span>}
      </div>
      {cfData.length > 0 && <AiCertaintyNote data={cfData} />}
      {cfData.length > 0 && <div style={{ marginTop: 18 }}>
        <div className="aii-sec-sub" style={{ marginBottom: 8, fontWeight: 800 }}>{t('adv.cf.mathTitle')}</div>
        <AiForecastMath data={cfData} />
      </div>}
      <div className="aii-cf-commentary">
        <Icon name="alertCircle" size={20} />
        <p>{advisor?.cashflowCommentary || t('adv.cf.commentaryFallback')}</p>
      </div>
    </Card>
    </section>

    </React.Fragment>}

    {tab === 'salg' && window.AiSalgTab && <window.AiSalgTab year={year} onNav={setRoute}
      financials={advisor?.financials || null} liquidity={hero?.balance ?? null} />}

    {tab === 'budsjett' && window.AiPlanTab && <window.AiPlanTab year={year} onNav={setRoute} />}

    {tab === 'kostnader' && <React.Fragment>
      <AiVerdict verdict={verdicts.kostnader} />
      {window.AiCostTab && <window.AiCostTab year={year} country={advisorCountry}
        income={advisor?.financials?.income ?? 0} fixedCostBasis={fixedCostBasis}
        moves={moves.kostnader} recommendations={recommendations} liquidityMoves={moves.likviditet}
        onAct={(move) => move.rec ? openRecommendation(move.rec) : move.tab === 'salg' ? setTab('salg') : act(move)}
        onOpenScenario={() => setTab('likviditet')} />}
    </React.Fragment>}

    {tab === 'skatt' && <React.Fragment>
    <AiVerdict verdict={verdicts.skatt} />
    {/* Valgkortene ligger først; beregningene og satsene er alltid synlige under. */}
    {window.AiiTaxChoices && <window.AiiTaxChoices choices={taxAdvice?.choices || []} />}
    {taxAdvice && window.AiiTaxInsights && <window.AiiTaxInsights insights={taxAdvice.insights}
      onPurchase={(item) => {
        const expense = window.PlummeBackend?.appState?.expenses?.find((entry) => entry.id === item.id);
        if (!expense) { setRoute && setRoute('allebilag'); return; }
        window.__plummePurchaseReturnRoute = 'aiinnsikt';
        if (item.kind === 'posting') window.__plummeEditManualPosting = expense;
        else window.__plummeEditPurchase = expense;
        setRoute && setRoute(item.kind === 'posting' ? 'fripostering' : 'nyttkjop');
      }} onPlan={() => setTab('budsjett')}
      onTaxReturn={() => setRoute && setRoute('skattemelding')} />}
    {window.AiiTaxProposals && <window.AiiTaxProposals advice={taxAdvice} onPlan={() => setTab('budsjett')} />}
    {window.AiiDeductionHunter && <window.AiiDeductionHunter year={year} country={advisorCountry} onGo={(route) => {
      if (route === 'nyttkjop') { window.__plummePurchaseReturnRoute = 'aiinnsikt'; window.__plummeAdvisorReturnTab = 'skatt'; }
      setRoute?.(route);
    }} open={hunterOpen} onOpen={() => setHunterOpen(true)} onClose={() => setHunterOpen(false)} />}
    </React.Fragment>}

    {tab === 'innsikt' && <React.Fragment>
    <div className="aii-innsikt-controls aii-choice-seg">
      <Segmented value={insightView} onChange={setInsightView}
        options={[{ value: 'rapport', label: t('adv.tab.reports') }, { value: 'bransje', label: t('adv.tab.benchmark') }]} />
    </div>

    {insightView === 'rapport' && <React.Fragment>
    {window.AiReport
      ? <window.AiReport key={year} year={year} mode={reportMode} onModeChange={setReportMode} />
      : <Card><EmptyState noVisual acc="doc" title={t('adv.report.emptyTitle')} text={t('adv.report.emptyText')} compact /></Card>}
    </React.Fragment>}

    {insightView === 'bransje' && <React.Fragment>
    {/* Bransjesammenligning. Grunnlaget vises alltid, selve sammenligningen bare når
        bransjeindeksen faktisk har tall for denne bransjen og størrelsesgruppen. */}
    {window.AiiIndustryBasis && <window.AiiIndustryBasis basis={industryBasis} />}
    {industryBasis?.availability === 'available' && window.AiiIndustryComparison
      ? <window.AiiIndustryComparison basis={industryBasis} />
      : window.AiiIndustryEmpty && <window.AiiIndustryEmpty basis={industryBasis} onNav={setRoute} />}
    </React.Fragment>}
    </React.Fragment>}

    {tab === 'lagrede-rapporter' && window.AiiSavedReports && <window.AiiSavedReports refreshKey={reportRefresh}
      onCreate={() => setReportOpen(true)} onChanged={() => setReportRefresh((value) => value + 1)} />}
    <SuperPlummaAdvisorChat open={chatOpen} onClose={() => setChatOpen(false)}
      context="bedrift" ask={askAdvisorControl} setRoute={setRoute} />
    {window.AiiTaskCenterModal && <window.AiiTaskCenterModal open={taskCenterOpen} onClose={() => setTaskCenterOpen(false)}
      buckets={buckets} onAct={openRecommendation} />}
    {taskFlow && <window.AiiTaskWorkflow key={advisorRequestKey + ':' + (taskFlow.id || taskFlow.route || taskFlow.tab)} task={taskFlow} year={year}
      onMarkDone={globalThis.PlummeAiAdvisorStatus.isDailyTask(taskFlow) && statusOf(taskFlow)?.status !== 'done' ? () => setStatus(taskFlow, { status: 'done' }) : undefined}
      advisor={advisor} bufferInfo={bufferInfo} onClose={() => { setTaskFlow(null); setTaskCenterOpen(true); }} onChanged={refreshAdvisorAfterBufferSave} />}
    {window.AiiReportModal && <window.AiiReportModal open={reportOpen} onClose={() => setReportOpen(false)}
      onSaved={() => setReportRefresh((value) => value + 1)} />}
    {window.AiiAnalysisModal && <window.AiiAnalysisModal open={analysisOpen} onClose={() => setAnalysisOpen(false)}
      panel={window.AiGeneratedInsightPanel
        ? <window.AiGeneratedInsightPanel onOpen={(target) => { setAnalysisOpen(false); openRecommendation(target); }} fallbackTasks={visibleTasks} insights={currentGeneratedInsights} loading={generatedInsightsLoading} error={generatedInsightsError}
          onRefresh={loadGeneratedInsights} onAlreadyCurrent={(kind, reason) => {
            if (!window.toast) return;
            const messageKey = window.PlummeAdvisorInsightPresentation.notice(currentGeneratedInsights?.[kind], kind);
            toast(t(messageKey), 'info');
          }} />
        : null} />}
  </div>;
}

// AiSecHead deles med aiinsikt-salg.jsx, som er en egen fil fordi denne allerede
// ligger over filstørrelsesgrensen.
Object.assign(window, { AiInnsiktPage, SuperPlummaAdvisorChat, aiiReply, AiSecHead, AiCashflowChart, AiiStatusPill, AiCountUp, aiHealthTone, AiiHelpTerm });
window.dispatchEvent?.(new CustomEvent('plumme-page-registered', { detail: { pages: ['AiInnsiktPage'] } }));
